import { ChatFileChip } from "src/unifiedCopilot/chat/attachments/ChatFileChip";
import type { ChatAttachment } from "src/unifiedCopilot/chat/attachments/useChatAttachments";
import { ContextualEntityChip } from "src/unifiedCopilot/chat/ContextualEntityChip";
import {
  type ContextualEntity,
  type ContextualEntityKey,
  contextualEntityKey,
} from "src/unifiedCopilot/context/types";

type Props = {
  /**
   * Automatic ambient context for the next turn, in registration order.
   * Entities registered with `dismissible: false` render without a ×.
   */
  contextualEntities: ContextualEntity[];
  onDismissContextualEntity: (key: ContextualEntityKey) => void;
  attachments: ChatAttachment[];
  onRemoveAttachment: (key: string) => void;
};

/**
 * The composer's chip row: automatic context pills first (dismissible unless
 * the entity opts out), then the files staged for the next message. Renders
 * nothing when there is neither.
 */
export const ContextChips: React.FC<Props> = ({
  contextualEntities,
  onDismissContextualEntity,
  attachments,
  onRemoveAttachment,
}) => {
  if (contextualEntities.length === 0 && attachments.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      data-loc="unified-copilot-context-chips"
    >
      {contextualEntities.map((entity) => {
        const key = contextualEntityKey(entity);
        const isDismissible = entity.dismissible !== false;
        return (
          <ContextualEntityChip
            key={key}
            dataLoc="unified-copilot-context-chip"
            entity={entity}
            maxWidth
            onRemove={
              isDismissible ? () => onDismissContextualEntity(key) : undefined
            }
          />
        );
      })}
      {attachments.map((attachment) => (
        <ChatFileChip
          key={attachment.key}
          contentType={attachment.file.type}
          dataLoc="unified-copilot-attachment-chip"
          isUploading={attachment.status === "uploading"}
          name={attachment.file.name}
          sizeBytes={attachment.file.size}
          onRemove={() => onRemoveAttachment(attachment.key)}
        />
      ))}
    </div>
  );
};
